import { WisdomErrorMessage } from '@modules/common/components/WisdomErrorMessage';
import { AssistanceResponseYesButton } from '@modules/oracle/components/AssistanceResponseYesButton';
import { OracleSearchNoButton } from '@modules/oracle/components/OracleConversationResponse/OracleSearchNoButton';
import { RegenerateResponseButton } from '@modules/oracle/components/RegenerateResponseButton';
import {
  useFlagAssistantResponse,
  useGetPromptPosition,
} from '@modules/oracle/hooks';
import { useIsWisdomFollowUpsEnabled } from '@modules/oracle/hooks/useIsWisdomFollowUpsEnabled';
import { OracleSearchActionType } from '@modules/oracle/store/oracleActionType';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import { OraclePrompt } from '@modules/oracle/store/types';
import { trackWisdomEvents } from '@modules/oracle/utils/trackWisdomEvents';
import { Box, Collapse, Divider, Stack, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useToggle } from 'react-use';
import { CopyMarkdownButton } from '../CopyMarkdownButton';
import { WisdomFollowUpQuestions } from '../WisdomFollowUpQuestions';

interface OracleConversationResponseFooterProps {
  answerMarkdown?: string;
  prompt: OraclePrompt;
  isReadOnly?: boolean;
}

export function OracleConversationResponseFooter({
  prompt,
  isReadOnly = false,
  answerMarkdown,
}: OracleConversationResponseFooterProps) {
  const promptID = prompt.id;
  const dispatch = useOracleSearchStore(({ dispatch }) => dispatch);
  const sessionID = useOracleSearchStore(({ data }) => data.sessionID);
  const likeSet = useOracleSearchStore(({ visual }) => visual.likeSet);
  const viewToken = useOracleSearchStore(({ data }) => data.chat?.editToken);
  const isResponseYes = likeSet.has(promptID);
  const dislikeSet = useOracleSearchStore(({ visual }) => visual.dislikeSet);
  const isResponseNo = dislikeSet.has(promptID);
  const [flagAssistantResponse] = useFlagAssistantResponse();
  const [shouldPlayResponseYesAnimation, toggleShouldPlayResponseYesAnimation] =
    useToggle(false);
  const threadPosition = useGetPromptPosition(promptID);

  const isWisdomFollowUpsEnabled = useIsWisdomFollowUpsEnabled();

  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const onThumbUpButtonClick = async () => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    await flagAssistantResponse({
      isResponseGood: true,
      responseID: promptID,
      viewToken,
      sessionID,
    });

    toggleShouldPlayResponseYesAnimation();

    dispatch({
      type: OracleSearchActionType.SET_VISUAL,
      payload: {
        likeSet: new Set([...likeSet, promptID]),
      },
    });

    animationTimeoutRef.current = setTimeout(() => {
      toggleShouldPlayResponseYesAnimation();
    }, 5 * 1000); // the animation will be there for 5 seconds

    trackWisdomEvents({
      eventName: 'Wisdom - Feedback',
      sessionID,
      threadPosition,
      isThumbUp: true,
      responseID: promptID,
    });
  };

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const shouldShowFollowupQuestions =
    isWisdomFollowUpsEnabled &&
    !isReadOnly &&
    !prompt.error &&
    prompt.endOfResponse &&
    answerMarkdown;

  const yesResponseButton =
    isResponseNo || !answerMarkdown ? null : (
      <AssistanceResponseYesButton
        responseID={promptID}
        onClick={onThumbUpButtonClick}
        disabled={isResponseYes}
        size="small"
        sx={{
          color: !isResponseYes ? 'primary.main' : 'text.secondary',
        }}
        shouldShowSpark={shouldPlayResponseYesAnimation}
      />
    );

  const noResponseButton =
    isResponseYes || !answerMarkdown ? null : (
      <Box>
        <Collapse in={!shouldPlayResponseYesAnimation} orientation="horizontal">
          <OracleSearchNoButton responseID={promptID} disabled={isResponseNo} />
        </Collapse>
      </Box>
    );

  const regenerateResponseButton =
    !isReadOnly && (!!prompt.error || prompt.endOfResponse) ? (
      <RegenerateResponseButton
        question={prompt.question}
        promptID={prompt.id}
        variant={!!prompt.error ? 'button' : 'icon'}
      />
    ) : null;

  const wsidomFollowUpQuestions = shouldShowFollowupQuestions ? (
    <WisdomFollowUpQuestions
      sessionID={sessionID}
      prompt={prompt.question}
      answerMarkdown={answerMarkdown}
    />
  ) : null;

  return (
    <Stack spacing={2} alignItems="flex-start">
      <Stack direction="row" spacing={1} alignItems="center">
        {answerMarkdown && (
          <Typography variant="caption" color="text.secondary">
            Is this helpful?
          </Typography>
        )}
        {yesResponseButton}
        {noResponseButton}
        <Divider orientation="vertical" variant="middle" flexItem />
        {answerMarkdown && <CopyMarkdownButton prompt={prompt} />}
        {regenerateResponseButton}
        {!!prompt.error ? <WisdomErrorMessage /> : null}
      </Stack>
      {wsidomFollowUpQuestions}
    </Stack>
  );
}
